import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AudioPlayerProvider } from './contexts/AudioPlayerContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import ProtectedRoute from './components/ProtectedRoute';
import AudioPlayer from './components/AudioPlayer';
import { Toaster } from './components/ui/sonner';
import {
  GlobeHemisphereWest,
  CheckCircle,
  X,
} from '@phosphor-icons/react';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Language from './pages/Language';
import VerifyEmail from './pages/VerifyEmail';
import AuthCallback from './pages/AuthCallback';
import Discover from './pages/Discover';
import Search from './pages/Search';
import StationDetail from './pages/StationDetail';
import Favorites from './pages/Favorites';
import Profile from './pages/Profile';
import PublicProfile from './pages/PublicProfile';
import Settings from './pages/Settings';
import Notifications from './pages/Notifications';
import AdminDashboard from './pages/AdminDashboard';
import Impressum from './pages/Impressum';
import PrivacyPolicy from './pages/PrivacyPolicy';

import './App.css';

function PublicLanguageButton() {
  const location = useLocation();
  const { user } = useAuth();
  const { language, setLanguage, languages, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const pagesWithNavbar = [
    '/',
    '/discover',
    '/search',
    '/favorites',
    '/profile',
    '/settings',
    '/notifications',
    '/admin',
    '/language',
    '/impressum',
    '/datenschutz',
  ];

  const shouldHide =
    user ||
    pagesWithNavbar.includes(location.pathname) ||
    location.pathname.startsWith('/station/') ||
    location.pathname.startsWith('/user/');

  if (shouldHide) return null;

  const handleLanguageSelect = (code) => {
    setLanguage(code);
    setIsOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="fixed top-4 left-4 z-[9998] flex items-center gap-2 rounded-full border border-white/10 bg-black/60 px-4 py-2 text-sm font-bold text-gray-300 backdrop-blur-xl hover:text-cyan-400 hover:border-cyan-400/50 transition-all"
      >
        <GlobeHemisphereWest size={20} />
        {t.navLanguage || 'Language'}
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center px-4">
          <div className="w-full max-w-md max-h-[78vh] rounded-t-3xl md:rounded-3xl border border-white/10 bg-[#070A10] shadow-[0_20px_80px_rgba(0,0,0,0.65)] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <GlobeHemisphereWest
                  size={26}
                  weight="duotone"
                  className="text-cyan-400"
                />

                <div>
                  <h2
                    className="text-lg font-black text-white tracking-tight"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {t.chooseLanguage || 'Choose language'}
                  </h2>

                  <p className="text-xs text-gray-400">
                    {t.displayLanguageFor || 'Display language for WORLDofRADIO'}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white flex items-center justify-center transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="max-h-[58vh] overflow-y-auto">
              {languages.map((item, index) => {
                const isActive = language === item.code;

                return (
                  <button
                    key={item.code}
                    type="button"
                    onClick={() => handleLanguageSelect(item.code)}
                    className={`w-full flex items-center justify-between px-5 py-4 text-left transition-all ${isActive
                      ? 'bg-cyan-400/10 text-cyan-300'
                      : 'text-gray-300 hover:bg-white/5 hover:text-cyan-300'
                      } ${index !== languages.length - 1
                        ? 'border-b border-white/10'
                        : ''
                      }`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-12 h-8 rounded-full flex items-center justify-center text-xs font-black border ${isActive
                          ? 'border-cyan-400 bg-cyan-400 text-black'
                          : 'border-white/10 bg-white/5 text-gray-400'
                          }`}
                      >
                        {item.label}
                      </div>

                      <div>
                        <div className="text-sm font-bold">{item.name}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {t.tapToSelect || 'Tap to select'}
                        </div>
                      </div>
                    </div>

                    {isActive && (
                      <CheckCircle
                        size={22}
                        weight="fill"
                        className="text-cyan-400 flex-shrink-0"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <PublicLanguageButton />

      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/impressum" element={<Impressum />} />
        <Route path="/datenschutz" element={<PrivacyPolicy />} />

        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/language" element={<Language />} />
        <Route path="/verify-email" element={<VerifyEmail />} />

        <Route path="/discover" element={<Discover />} />
        <Route path="/search" element={<Search />} />
        <Route path="/station/:id" element={<StationDetail />} />

        <Route
          path="/favorites"
          element={
            <ProtectedRoute>
              <Favorites />
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />

        <Route
          path="/user/:username"
          element={
            <ProtectedRoute>
              <PublicProfile />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
      </Routes>

      <AudioPlayer />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <AudioPlayerProvider>
              <AppRouter />
              <Toaster />
            </AudioPlayerProvider>
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;